/**
 * Agentic Session Service
 *
 * API calls for:
 * - Fetching user sessions
 * - Finding CHAT_CONVERSATION sessions
 * - Deleting agentic sessions
 */

import { httpClient } from '../utils/httpClient';
import {
  GET_USER_SESSION_URL,
  DELETE_AGENTIC_SESSION,
} from '../constants/apiRoutes';

interface HttpClientResponse<T> {
  success: boolean;
  status: number | null;
  statusText: string;
  message: string;
  data: T | null;
  errors: unknown[];
}

/** Individual session data from user sessions API */
interface UserSessionItem {
  id: string;
  type: string;
  state: string;
  fileName?: string;
  isLastActiveSession: boolean;
}

/** User session data structure */
interface UserSessionData {
  id: string;
  activeSessions: UserSessionItem[];
}

/** Response from user session API */
interface UserSessionResponseData {
  userSessionData: UserSessionData;
}

/** Result from findAndDeleteChatSession */
export interface FindAndDeleteResult {
  success: boolean;
  sessionDeleted: boolean;
  error?: string;
}

/**
 * Fetches all user sessions
 * @returns Array of user sessions or null on error
 */
export async function fetchUserSessions(): Promise<UserSessionItem[] | null> {
  try {
    const response = (await httpClient.get(
      GET_USER_SESSION_URL()
    )) as HttpClientResponse<UserSessionResponseData>;

    if (response.success && response.data?.userSessionData?.activeSessions) {
      return response.data.userSessionData.activeSessions;
    }

    console.warn(
      '[AgenticSession] Failed to fetch user sessions:',
      response.message
    );
    return null;
  } catch (error) {
    console.error('[AgenticSession] Error fetching user sessions:', error);
    return null;
  }
}

/**
 * Find a CHAT_CONVERSATION session from the user sessions
 * @returns The chat session or null if not found
 */
export async function findChatSession(): Promise<UserSessionItem | null> {
  const sessions = await fetchUserSessions();

  if (!sessions) {
    return null;
  }

  const chatSession = sessions.find(
    session => session.type === 'CHAT_CONVERSATION'
  );

  return chatSession || null;
}

/**
 * Deletes an agentic session by ID
 * @param sessionId - The session ID to delete
 * @returns True if deletion was successful, false otherwise
 */
export async function deleteAgenticSession(
  sessionId: string
): Promise<boolean> {
  try {
    const response = (await httpClient.delete(
      DELETE_AGENTIC_SESSION(sessionId)
    )) as HttpClientResponse<unknown>;

    if (response.success) {
      console.log('[AgenticSession] Session deleted successfully:', sessionId);
      return true;
    }

    console.warn(
      '[AgenticSession] Failed to delete session:',
      response.message
    );
    return false;
  } catch (error) {
    console.error('[AgenticSession] Error deleting session:', error);
    return false;
  }
}

/**
 * Find and delete any existing CHAT_CONVERSATION session
 * Used when starting a new agentic planner session
 *
 * @returns Result object with success status and whether a session was deleted
 */
export async function findAndDeleteChatSession(): Promise<FindAndDeleteResult> {
  try {
    const chatSession = await findChatSession();

    if (!chatSession) {
      console.log(
        '[AgenticSession] No existing CHAT_CONVERSATION session found'
      );
      return { success: true, sessionDeleted: false };
    }

    console.log(
      '[AgenticSession] Found existing CHAT_CONVERSATION session:',
      chatSession.id
    );

    const deleted = await deleteAgenticSession(chatSession.id);

    return {
      success: true,
      sessionDeleted: deleted,
      error: deleted ? undefined : 'Failed to delete session',
    };
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : 'Unknown error';
    console.error('[AgenticSession] Error in findAndDeleteChatSession:', error);
    return {
      success: false,
      sessionDeleted: false,
      error: errorMessage,
    };
  }
}
